import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Save,
    X,
    Calendar,
    Clock,
    BookOpen
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

interface PtLesson {
    id: string;
    created_at: string;
    teacher_id: string;
    student_id: string;
    lesson_date: string;
    start_time: string;
    end_time: string;
    duration_minutes: number;
    lesson_type: string;
    subject: string | null;
    notes: string | null;
    status: string;
    pt_teachers?: {
        full_name: string;
    };
    pt_students?: {
        full_name: string;
    };
}

interface Teacher {
    id: string;
    full_name: string;
}

interface Student {
    id: string;
    full_name: string;
    teacher_id: string;
}

const TeacherResponses: React.FC = () => {
    const [lessons, setLessons] = useState<PtLesson[]>([]);
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);

    const [formData, setFormData] = useState<Partial<PtLesson>>({
        teacher_id: '',
        student_id: '',
        lesson_date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '',
        end_time: '',
        lesson_type: 'private',
        subject: '',
        notes: '',
        status: 'completed'
    });

    useEffect(() => {
        fetchLessons();
        fetchTeachers();
        fetchStudents();
    }, []);

    const fetchTeachers = async () => {
        try {
            const { data, error } = await supabase
                .from('pt_teachers')
                .select('id, full_name')
                .order('full_name');

            if (error) throw error;
            setTeachers(data || []);
        } catch (error) {
            console.error('Error fetching teachers:', error);
        }
    };

    const fetchStudents = async () => {
        try {
            const { data, error } = await supabase
                .from('pt_students')
                .select('id, full_name, teacher_id')
                .eq('is_active', true)
                .order('full_name');

            if (error) throw error;
            setStudents(data || []);
        } catch (error) {
            console.error('Error fetching students:', error);
        }
    };

    const fetchLessons = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('pt_lessons')
                .select('*, pt_teachers(full_name), pt_students(full_name)')
                .order('lesson_date', { ascending: false })
                .order('start_time', { ascending: false });

            if (error) throw error;
            setLessons(data || []);
        } catch (error) {
            console.error('Error fetching lessons:', error);
            toast.error('Dersler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (!formData.teacher_id || !formData.student_id) {
                toast.error('Lütfen öğretmen ve öğrenci seçin');
                return;
            }

            if (editingId) {
                const { error } = await supabase
                    .from('pt_lessons')
                    .update(formData)
                    .eq('id', editingId);

                if (error) throw error;
                toast.success('Ders güncellendi');
            } else {
                const { error } = await supabase
                    .from('pt_lessons')
                    .insert([formData]);

                if (error) throw error;
                toast.success('Ders eklendi');
            }

            setShowModal(false);
            setEditingId(null);
            setFormData({
                lesson_date: format(new Date(), 'yyyy-MM-dd'),
                start_time: '',
                end_time: '',
                lesson_type: 'private',
                status: 'completed'
            });
            fetchLessons();
        } catch (error) {
            console.error('Error saving lesson:', error);
            toast.error('Kaydedilirken hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu ders kaydını silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('pt_lessons')
                .delete()
                .eq('id', id);

            if (error) throw error;
            toast.success('Ders silindi');
            fetchLessons();
        } catch (error) {
            console.error('Error deleting lesson:', error);
            toast.error('Silinirken hata oluştu');
        }
    };

    const handleEdit = (lesson: PtLesson) => {
        setFormData({
            teacher_id: lesson.teacher_id,
            student_id: lesson.student_id,
            lesson_date: lesson.lesson_date,
            start_time: lesson.start_time,
            end_time: lesson.end_time,
            lesson_type: lesson.lesson_type,
            subject: lesson.subject,
            notes: lesson.notes,
            status: lesson.status
        });
        setEditingId(lesson.id);
        setShowModal(true);
    };

    const filteredLessons = lessons.filter(l =>
        (l.pt_teachers?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.pt_students?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (l.subject || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter students based on selected teacher in form
    const availableStudents = formData.teacher_id
        ? students.filter(s => s.teacher_id === formData.teacher_id)
        : [];

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Öğretmen Yanıtları</h1>
                <button
                    onClick={() => {
                        setEditingId(null);
                        setFormData({
                            teacher_id: '',
                            student_id: '',
                            lesson_date: format(new Date(), 'yyyy-MM-dd'),
                            start_time: '',
                            end_time: '',
                            lesson_type: 'private',
                            status: 'completed'
                        });
                        setShowModal(true);
                    }}
                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    <span>Yeni Yanıt Ekle</span>
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Öğretmen, öğrenci veya ders ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                <th className="px-6 py-3">Tarih</th>
                                <th className="px-6 py-3">Öğretmen Adı</th>
                                <th className="px-6 py-3">Öğrenci</th>
                                <th className="px-6 py-3">Ders</th>
                                <th className="px-6 py-3">Başlangıç</th>
                                <th className="px-6 py-3">Bitiş</th>
                                <th className="px-6 py-3">Süre</th>
                                <th className="px-6 py-3">Tür</th>
                                <th className="px-6 py-3">Durum</th>
                                <th className="px-6 py-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                                        Yükleniyor...
                                    </td>
                                </tr>
                            ) : filteredLessons.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-6 py-8 text-center text-gray-500">
                                        Kayıt bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                filteredLessons.map((lesson) => (
                                    <tr key={lesson.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {format(new Date(lesson.lesson_date), 'dd.MM.yyyy')}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                            {lesson.pt_teachers?.full_name || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.pt_students?.full_name || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.subject || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.start_time}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.end_time}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.duration_minutes ? `${lesson.duration_minutes} dk` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                                            {lesson.lesson_type === 'private' ? 'Özel Ders' : 'Grup Dersi'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {lesson.status === 'completed' ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                                    Tamamlandı
                                                </span>
                                            ) : lesson.status === 'cancelled' ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                                    İptal
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                                    Planlandı
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() => handleEdit(lesson)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(lesson.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingId ? 'Yanıtı Düzenle' : 'Yeni Yanıt Ekle'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Öğretmen</label>
                                <select
                                    required
                                    value={formData.teacher_id || ''}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            teacher_id: e.target.value,
                                            student_id: '' // Reset student when teacher changes
                                        });
                                    }}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                >
                                    <option value="">Öğretmen Seçin</option>
                                    {teachers.map(t => (
                                        <option key={t.id} value={t.id}>{t.full_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Öğrenci</label>
                                <select
                                    required
                                    value={formData.student_id || ''}
                                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    disabled={!formData.teacher_id}
                                >
                                    <option value="">
                                        {formData.teacher_id ? 'Öğrenci Seçin' : 'Önce Öğretmen Seçin'}
                                    </option>
                                    {availableStudents.map(s => (
                                        <option key={s.id} value={s.id}>{s.full_name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tarih</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            required
                                            type="date"
                                            value={formData.lesson_date || ''}
                                            onChange={(e) => setFormData({ ...formData, lesson_date: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ders Konusu</label>
                                    <div className="relative">
                                        <BookOpen className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={formData.subject || ''}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                            placeholder="Matematik, vb."
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Başlangıç</label>
                                    <div className="relative">
                                        <Clock className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            required
                                            type="time"
                                            value={formData.start_time || ''}
                                            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Bitiş</label>
                                    <div className="relative">
                                        <Clock className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                                        <input
                                            required
                                            type="time"
                                            value={formData.end_time || ''}
                                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                                            className="w-full pl-9 p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ders Türü</label>
                                    <select
                                        value={formData.lesson_type || 'private'}
                                        onChange={(e) => setFormData({ ...formData, lesson_type: e.target.value })}
                                        className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        <option value="private">Özel Ders</option>
                                        <option value="group">Grup Dersi</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Durum</label>
                                    <select
                                        value={formData.status || 'completed'}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        <option value="completed">Tamamlandı</option>
                                        <option value="scheduled">Planlandı</option>
                                        <option value="cancelled">İptal</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notlar</label>
                                <textarea
                                    rows={3}
                                    value={formData.notes || ''}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                                >
                                    <Save className="w-4 h-4" />
                                    <span>Kaydet</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherResponses;
